import type { CaptureResult } from 'posthog-js'

/**
 * Shared classification of "no-signal" errors, used by both capture paths:
 *
 * - lib/errorCapture.ts — the explicit captureError()/Sentry path.
 * - lib/posthog.ts — PostHog's exception *autocapture* (capture_unhandled_*),
 *   which never runs through captureError(), so unhandled browser and
 *   third-party noise (ResizeObserver reflow, ad-blocked gtag, iOS webview
 *   message handlers) has to be dropped here via before_send.
 *
 * Keeping the rules in one place means both paths stay in sync.
 */
export const NOISE_PATTERNS: RegExp[] = [
    /ResizeObserver loop/i,
    /^Script error\.?$/,
    /AbortError|Request aborted|canceled/i,
    /Unauthenticated|status code 401|status code 419/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    // Third-party tag loader blocked by ad/tracking blockers — never our code.
    /googletagmanager\.com|gtag\/js/i,
    // iOS in-app webviews inject window.webkit.messageHandlers; touching it
    // throws in normal browsers and is not our code.
    /webkit\.messageHandlers/i,
]

/**
 * HTTP statuses that are expected, user-driven, or already handled elsewhere
 * and would only flood the tracker: 401/419 session expiry (bootstrap.js
 * redirects), 422 form validation (surfaced inline to the user).
 *
 * Deliberately NOT suppressed: 403 (a broken policy denying legit users would
 * spike here), 404/500/504, and axios "Network Error" (a CORS/mixed-content
 * or deploy regression looks identical to steady-state connectivity noise).
 * Those can all carry a real regression signal, so they stay visible.
 */
export const NOISE_STATUSES = new Set([401, 419, 422])

export function matchesNoisePattern(message: string): boolean {
    return NOISE_PATTERNS.some(pattern => pattern.test(message))
}

/**
 * Extracts the human-readable message from a PostHog `$exception` event's
 * properties, checking the structured exception list first and falling back
 * to the flat message property.
 */
function exceptionMessageOf(event: CaptureResult): string {
    const list = event.properties?.$exception_list
    const fromList =
        Array.isArray(list) && typeof list[0]?.value === 'string'
            ? (list[0].value as string)
            : undefined
    const flat = event.properties?.$exception_message
    return fromList ?? (typeof flat === 'string' ? flat : '')
}

/**
 * PostHog `before_send` hook. Drops autocaptured `$exception` events whose
 * message is known noise; passes every other event through untouched.
 */
export function dropNoisyExceptionEvents(
    event: CaptureResult | null
): CaptureResult | null {
    if (event?.event !== '$exception') {
        return event
    }
    return matchesNoisePattern(exceptionMessageOf(event)) ? null : event
}
